import { useCallback } from 'react'

import { getScoreIdParts } from '@ilg-totallymoney/cardloanutils'
import {
    CardsDownsellProduct,
    LoanProductCategory,
} from '@ilg-totallymoney/types'
import { useAtomValue } from 'jotai'

import { ScoreId } from '@appTypes/App.interface'
import {
    FormEventClickout,
    FormEventOutcome,
} from '@appTypes/BasinEvents.interface'
import { ScoredLoanProduct } from '@appTypes/PagesApi.interface'

import { acquisitionTrackingAtom } from '@atoms/acquisitionTracking'

import { encode } from '@utils/base64url'
import { getAppConfig } from '@utils/getAppConfig'
import { getLoanPurposeEncoded } from '@utils/getLoanPurposeEncoded'
import { getMarketingTrackingData } from '@utils/getMarketingTrackingData'
import { getSessionId } from '@utils/getSessionId'
import getTopLoansEventData from '@utils/getTopThreeLoansEventData'

import useAppQueryState from './useAppQueryState'
import { useBasin } from './useBasin'
import useFeatureFlag from './useFeatureFlag'
import useFeatureFlags from './useFeatureFlags'
import useGoogleTagManager from './useGoogleTagManager'
import useRequestedLoanDetails from './useRequestedLoanDetails'
import { getFeatureFlagsAsArray } from './utils/getFeatureFlagsAsArray'

const useLogEvents = () => {
    const appConfig = getAppConfig()
    const { send, ids } = useBasin()
    const { appQueryState } = useAppQueryState()
    const gtmSend = useGoogleTagManager()
    const { featureFlags } = useFeatureFlags()
    const acquisitionTracking = useAtomValue(acquisitionTrackingAtom)
    const {
        scoreId,
        loanAmount: loanAmountRequested,
        loanTerm: loanTermRequested,
    } = useRequestedLoanDetails()

    const trackingData = getMarketingTrackingData(appQueryState)

    const featureFlagAcquisitionJourneyEnableDatalayer = useFeatureFlag(
        'acquisitionJourneyEnableDatalayer'
    )
    const variations = getFeatureFlagsAsArray(featureFlags)

    const logVisitEvent = useCallback(
        ({ category }: { category: string }) => {
            const eventData = {
                Url: window.location.href,
                Referrer: document.referrer,
                UserAgent: navigator.userAgent,
                Whitelabel: appConfig.NAME_WHITELABEL,
                Variation: variations.join(','),
                Category: category,
                ScoreId: appQueryState.scoreId!,
                CustomerId: appQueryState.customerId!,
                CustomerType: appQueryState.customerType!,
                IsKnownCustomer: appQueryState.isKnownCustomer!,

                SendId: trackingData.sendId,
                mediasource: trackingData.m,
                campaign: trackingData.cam,
                keyword: trackingData.k,
                googleClickId: trackingData.gclid,
                adgroup: trackingData.adg,
            }

            send('Visit', eventData)
        },
        [send, appQueryState, trackingData]
    )

    const logLoansLoadedEvent = useCallback(() => {
        send('LoansLoaded', {
            Url: window.location.href,
            CustomerId: appQueryState.customerId!,
            Campaign: trackingData.cam,
        })
    }, [send, appQueryState])

    const logCheckEligibilityEvent = useCallback(
        ({
            category,
            loanAmount,
            loanTerm,
            origin,
        }: {
            category: LoanProductCategory
            loanAmount: number
            loanTerm: number
            origin: string
        }) => {
            send('FormOpen', {
                Origin: origin,
                Category: category,
                LoanAmount: loanAmount,
                LoanTerm: loanTerm,
                Whitelabel: appConfig.NAME_WHITELABEL,
                ApiClientName: appConfig.NAME_WHITELABEL,
                customerId: appQueryState.customerId!,
                customerType: appQueryState.customerType!,
            })
        },
        [send]
    )

    const logLoansResultsEmailTriggerEvent = useCallback(
        ({
            loans,
            loanPurpose,
            isNewAlgo,
        }: {
            isNewAlgo?: boolean
            loanPurpose: string
            loans: ScoredLoanProduct[] | null
        }) => {
            const topLoans = getTopLoansEventData(loans ?? [], isNewAlgo)

            if (topLoans == null || typeof window === 'undefined') {
                return
            }

            const { leadId, loanAmount, loanTerm } = getScoreIdParts(
                scoreId! as ScoreId
            )

            send('ResultsEmailTriggerV1', {
                ScoreId: scoreId!,
                ScoreIdEncoded: encode(scoreId!),
                LeadId: leadId,
                CustomerId: appQueryState.customerId!,
                Url: window.location.href,

                // TODO: Check with CRM if this value is used
                //WhitelabelDeepLinkUrl: getWhitelabelDeepLinkUrl(accountType),

                LoanAmount: loanAmount,
                LoanTerm: loanTerm,
                LoanPurpose: loanPurpose,
                LoanPurposeEncoded: getLoanPurposeEncoded(loanPurpose),
                ...topLoans,
            })
        },
        [scoreId, appQueryState.customerId]
    )

    const logScoredTableUpdateEvent = useCallback(
        ({
            category,
            sortedBy,
            products,
        }: {
            category: LoanProductCategory
            products: ScoredLoanProduct[]
            sortedBy: string
        }) => {
            const { loanAmount, loanTerm } = getScoreIdParts(
                scoreId! as ScoreId
            )

            const sortedProducts = products.map(product => {
                return {
                    productCode: product.loanId,
                    isSponsored: product.isSponsored,
                    hasMarketingOffer: product.hasMarketingOffer,
                }
            })

            send('ScoredTableUpdate', {
                customerId: appQueryState.customerId,
                category,
                loanAmount,
                loanTerm,
                sortedBy,
                sortedProducts,
            })
        },
        [appQueryState.customerId, scoreId, send]
    )

    const logSortChangeEvent = useCallback(
        ({ sortKey }: { sortKey: string }) => {
            send('Sort', {
                SortBy: sortKey,
                ApiClientName: appConfig.NAME_WHITELABEL,
                CustomerId: appQueryState.customerId!,
                CustomerType: appQueryState.customerType!,
            })
        },
        [send]
    )

    const logCategoryChangeEvent = useCallback(
        ({
            fromCategory,
            toCategory,
        }: {
            fromCategory: LoanProductCategory
            toCategory: LoanProductCategory
        }) => {
            send('ChangeTab', {
                existingCategory: fromCategory,
                category: toCategory,
                customerId: appQueryState.customerId,
                whitelabel: appConfig.NAME_WHITELABEL,
            })
        },
        [send]
    )

    const logResultsUpdateEvent = useCallback(
        ({
            loanAmount,
            loanTerm,
            loanPurpose,
        }: {
            loanAmount: number
            loanPurpose?: string
            loanTerm: number
        }) => {
            send('ResultsUpdate', {
                LoanAmount: loanAmount,
                LoanTerm: loanTerm,
                LoanPurpose: loanPurpose,
                ApiClientName: appConfig.NAME_WHITELABEL,
                CustomerId: appQueryState.customerId,
                CustomerType: appQueryState.customerType,
            })
        },
        [send]
    )

    const logPostClickEvent = useCallback(
        ({
            productCode,
            row,
            score,
            monthlyRepayment,
            totalChargeForCredit,
            totalAmountRepayable,
            representativeApr,
            isBestMatch,
            category,
        }: {
            category?: string
            isBestMatch?: boolean
            monthlyRepayment?: number
            productCode: string
            representativeApr?: number
            row: number
            score?: number
            totalAmountRepayable?: number
            totalChargeForCredit?: number
        }) => {
            send('PostClick', {
                ProductCode: productCode,
                Row: row,
                Score: score,
                MonthlyRepayment: monthlyRepayment,
                TotalChargeForCredit: totalChargeForCredit,
                TotalAmountRepayable: totalAmountRepayable,
                RepresentativeApr: representativeApr,
                CustomerId: appQueryState.customerId,
                CustomerType: appQueryState.customerType,
                IsKnownCustomer: appQueryState.isKnownCustomer,
                ApiClientName: appConfig.NAME_WHITELABEL,
                googleClickId: trackingData.gclid,
                BestMatch: isBestMatch,
                Category: category,
            })
        },
        [send]
    )

    const logModalOceanSuccessEvent = useCallback(
        ({ productCode }: { productCode: string }) => {
            const { leadId } = getScoreIdParts(scoreId! as ScoreId)

            send('SecuredModalSuccess', {
                ScoreId: scoreId!,
                LeadId: leadId,
                CustomerId: appQueryState.customerId!,
                LoanAmount: loanAmountRequested,
                LoanTerm: loanTermRequested,
                LoanPurpose: appQueryState.loanPurpose,
                VisitGuid: getSessionId(),
                DailyId: ids?.dailyId,
                PermanentId: ids?.permanentId,
                ProductCode: productCode,
                partner: appConfig.NAME_WHITELABEL,
                ApiClientName: appConfig.NAME_WHITELABEL,
            })
        },
        [send, scoreId, loanAmountRequested, loanTermRequested]
    )

    const logCardsDownsellClickEvent = useCallback(
        ({
            cardToShow,
            numberOfOptions,
        }: {
            cardToShow: CardsDownsellProduct
            numberOfOptions: number
        }) => {
            send('DownsellClick', {
                VisitGuid: getSessionId(),
                DailyId: ids?.dailyId,
                PermanentId: ids?.permanentId,
                CardProductCode: cardToShow.productCode,
                Score: cardToShow.score,
                CustomerId: appQueryState.customerId!,
                NumberOfOptions: numberOfOptions,
                DownsellCategory: 'card',
            })
        },
        [send]
    )

    const logCardsDownsellShownEvent = useCallback(() => {
        send('CardsDownsellShown', {
            VisitGuid: getSessionId(),
            DailyId: ids?.dailyId,
            PermanentId: ids?.permanentId,
            CardsDownsellShown: true,
            CustomerId: appQueryState.customerId!,
            CustomerType: appQueryState.customerType!,
        })
    }, [send])

    const logAlternativeProductsVisit = useCallback(
        ({
            cardsDownsellShown,
            hasGuarantorLoans,
        }: {
            cardsDownsellShown: boolean
            hasGuarantorLoans: boolean
        }) => {
            const { leadId, loanAmount, loanTerm } = getScoreIdParts(
                scoreId! as ScoreId
            )

            send('AlternativeProductsVisit', {
                VisitGuid: getSessionId(),
                DailyId: ids?.dailyId,
                PermanentId: ids?.permanentId,
                Variation: variations.join(','),
                CardsDownsellShown: cardsDownsellShown,
                NoGuarantor: hasGuarantorLoans,
                ScoreId: scoreId!,
                ScoreIdEncoded: encode(scoreId!),
                LeadId: leadId,
                CustomerId: appQueryState.customerId!,
                LoanAmount: loanAmount,
                LoanTerm: loanTerm,
                CustomerType: appQueryState.customerType!,
                ApiClientName: appConfig.NAME_WHITELABEL,
            })
        },
        [send, scoreId]
    )

    const logIneligiblesEditLoanDetailsClickEvent = useCallback(
        ({
            loanAmount,
            loanTerm,
        }: {
            loanAmount: number
            loanTerm: number
        }) => {
            send('IneligiblesEditLoanDetailsClick', {
                loanAmount,
                loanTerm,
                customerId: appQueryState.customerId!,
            })
        },
        [send]
    )

    const logLoansAltEditDetailsShown = useCallback(
        () =>
            send('LoansAltEditDetailsShown', {
                customerId: appQueryState.customerId!,
            }),
        [send]
    )

    const logAcquisitionFormAccept = useCallback(
        ({ bestOffer, products, results, outcomeNumber }: FormEventOutcome) => {
            const basePayload = {
                event_name: 'form_accept',
                outcome: {
                    best_offer: bestOffer,
                    outcome: 'eligible',
                    outcome_number: outcomeNumber,
                    products: products.join('|'),
                    results,
                },
                event_details: {
                    ...acquisitionTracking?.basinPayload.event_details,
                    step: 'productTable',
                    step_url: window.location.href,
                    journey:
                        acquisitionTracking?.basinPayload.event_details
                            ?.journey ?? 'Loans Signup',
                },
            }

            send('form_accept', {
                ...acquisitionTracking?.basinPayload,
                ...basePayload,
            })

            if (featureFlagAcquisitionJourneyEnableDatalayer) {
                gtmSend({
                    ...acquisitionTracking?.dataLayerPayload,
                    ...basePayload,
                })
            }
        },
        [send]
    )

    const logAcquisitionFormDecline = useCallback(
        ({ bestOffer, products, results, outcomeNumber }: FormEventOutcome) => {
            const basePayload = {
                event_name: 'form_decline',
                outcome: {
                    best_offer: bestOffer,
                    outcome: 'ineligible',
                    outcome_number: outcomeNumber,
                    products: products.join('|'),
                    results,
                },
                event_details: {
                    ...acquisitionTracking?.basinPayload.event_details,
                    step: 'alternativeProductTable',
                    step_url: window.location.href,
                    journey:
                        acquisitionTracking?.basinPayload.event_details
                            ?.journey ?? 'Loans Signup',
                },
            }

            send('form_decline', {
                ...acquisitionTracking?.basinPayload,
                ...basePayload,
            })

            if (featureFlagAcquisitionJourneyEnableDatalayer) {
                gtmSend({
                    ...acquisitionTracking?.dataLayerPayload,
                    ...basePayload,
                })
            }
        },
        [send]
    )

    const logAcquisitionFormClickout = useCallback(
        ({
            clickout,
            outcome,
        }: {
            clickout: FormEventClickout
            outcome: FormEventOutcome
        }) => {
            const basePayload = {
                event_name: 'form_clickout',
                event_details: {
                    ...acquisitionTracking?.basinPayload.event_details,
                    step: 'productTable',
                    step_url: window.location.href,
                    journey:
                        acquisitionTracking?.basinPayload.event_details
                            ?.journey ?? 'Loans Signup',
                },
                clickout,
                outcome,
            }

            send('form_clickout', {
                ...acquisitionTracking?.basinPayload,
                ...basePayload,
            })

            if (featureFlagAcquisitionJourneyEnableDatalayer) {
                gtmSend({
                    ...acquisitionTracking?.dataLayerPayload,
                    ...basePayload,
                })
            }
        },
        [send]
    )

    return {
        logVisitEvent,

        logAlternativeProductsVisit,

        logLoansLoadedEvent,

        logCheckEligibilityEvent,

        logLoansResultsEmailTriggerEvent,

        logScoredTableUpdateEvent,

        logSortChangeEvent,
        logCategoryChangeEvent,

        logResultsUpdateEvent,

        logPostClickEvent,

        logModalOceanSuccessEvent,

        logCardsDownsellClickEvent,
        logCardsDownsellShownEvent,

        logIneligiblesEditLoanDetailsClickEvent,

        logLoansAltEditDetailsShown,

        logAcquisitionFormAccept,

        logAcquisitionFormDecline,

        logAcquisitionFormClickout,
    }
}

export default useLogEvents
